import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Choice, { Option } from '@components/choice/Choice'
import TextInput from '@components/textInput/TextInput'

import useReducer from '@store/useReducer'
import * as MetierStore from '@store/metier/metier'

import useI18n from '@i18n/useI18n'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'

interface Values {
  metierId: string
}

const Metier = ({ form, onForm }: ControleFormProps) => {
  const i18n = useI18n()

  const init: Values = React.useMemo(() => ({ metierId: form.metierId }), [form.metierId])

  const [search, setSearch] = React.useState('')

  const allMetiers = useReducer(MetierStore.store, (s) => s.metiers)

  const metiers = React.useMemo(
    () =>
      allMetiers
        .filter((m) => sanitize(m.libelle).includes(sanitize(search)))
        .filter((m) => m.societeIds.length === 0 || m.societeIds.includes(form.societeId)),
    [allMetiers, search, form.societeId]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const next: Partial<ControleForm> = {
      ...vals,
      localisationIds: [],
      reponses: [],
      completed: false,
      accompagnementPrestataire: undefined,
      type: undefined,
      commentaire: undefined,
    }

    onForm(next).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const options: Option[] = metiers.map((s) => ({ label: s.libelle, value: s.id }))
  const option = options.find((o) => o.value === values.metierId)

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.metier.search')}
          left={{ name: 'search' }}
        />
      </SearchContainer>

      {options.length === 0 ? (
        <Empty>{i18n.t('screens.controles.form.metier.empty')}</Empty>
      ) : (
        <Choice
          data={options}
          onItemSelected={(opt) => setFieldValue('metierId', opt.value)}
          value={option}
          inline={false}
        />
      )}
    </MainContainer>
  )
}

export default Metier

const MainContainer = styled(View)`
  margin: 0 20px;
  flex: 1;
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 25)}px;
  margin-bottom: 25px;
`

const Empty = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`
